import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Toast, Dialog } from 'react-vant'
import { ArrowLeft } from '@react-vant/icons'
import request from '../../api/request'
import { useSelector } from 'react-redux'
import { isPersonal } from '../../utils/permissions'
import './ClassMatchScorer.css'

// ========== 翻页数字组件 ==========
const SingleDigit = ({ digit }) => {
    const [currentDigit, setCurrentDigit] = useState(digit)
    const [nextDigit, setNextDigit] = useState(digit)
    const [isFlipping, setIsFlipping] = useState(false)

    useEffect(() => {
        if (digit !== currentDigit) {
            setNextDigit(digit)
            setIsFlipping(true)

            const timer = setTimeout(() => {
                setIsFlipping(false)
                setCurrentDigit(digit)
            }, 700)

            return () => clearTimeout(timer)
        }
    }, [digit, currentDigit])

    return (
        <div className={`flip-digit ${isFlipping ? 'flipping' : ''}`}>
            <div className="card-top"><span>{isFlipping ? nextDigit : currentDigit}</span></div>
            <div className="card-bottom"><span>{currentDigit}</span></div>
            <div className="card-top-flip"><span>{currentDigit}</span></div>
            <div className="card-bottom-flip"><span>{isFlipping ? nextDigit : currentDigit}</span></div>
        </div>
    )
}

const FlipNumber = ({ value, isSmall = false }) => {
    const valStr = String(value).padStart(2, '0')
    return (
        <div className={`flip-clock ${isSmall ? 'small' : ''}`}>
            {valStr.split('').map((d, i) => (
                <SingleDigit key={i} digit={d} />
            ))}
        </div>
    )
}

// ========== 倒计时组件 ==========
const CountdownTimer = ({ defaultMinutes = 20, initialSeconds, initialIsActive, scheduleId }) => {
    const [seconds, setSeconds] = useState(initialSeconds)
    const [isActive, setIsActive] = useState(initialIsActive === 1)
    const [isFinished, setIsFinished] = useState(false)

    // 同步计时器到后端
    const syncTimer = async (currentSeconds, running) => {
        try {
            await request.patch(`/v2/sports/class-match/schedules/${scheduleId}/live-score`, {
                timer_seconds: currentSeconds,
                timer_is_active: running ? 1 : 0
            })
        } catch (error) {
            console.error('同步计时器失败:', error)
        }
    }

    useEffect(() => {
        let interval = null
        let lastTick = Date.now()
        
        if (isActive && seconds > 0) {
            interval = setInterval(() => {
                const now = Date.now()
                const elapsed = Math.floor((now - lastTick) / 1000)
                
                if (elapsed >= 1) {
                    lastTick = now
                    setSeconds(s => {
                        const next = s - elapsed
                        if (next <= 0) {
                            setIsActive(false)
                            setIsFinished(true)
                            syncTimer(0, false)
                            return 0
                        }
                        // 仅在跨过5秒边界时同步，减少请求
                        if (Math.floor(s / 5) !== Math.floor(next / 5)) {
                            syncTimer(next, true)
                        }
                        return next
                    })
                }
            }, 500) // 采样频率提高到0.5s以保证流畅感，但逻辑基于时间戳
        } else {
            clearInterval(interval)
        }
        return () => clearInterval(interval)
    }, [isActive, seconds, scheduleId])

    const handleToggle = () => {
        const nextActive = !isActive
        setIsActive(nextActive)
        syncTimer(seconds, nextActive)
    }

    const addTime = (mins) => {
        const nextSeconds = Math.max(0, seconds + mins * 60)
        setSeconds(nextSeconds)
        setIsFinished(false)
        syncTimer(nextSeconds, isActive)
    }

    const reset = (mins = defaultMinutes) => {
        const nextSeconds = mins * 60
        setSeconds(nextSeconds)
        setIsActive(false)
        setIsFinished(false)
        syncTimer(nextSeconds, false)
    }

    const formatTime = () => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return {
            m1: Math.floor(mins / 10),
            m2: mins % 10,
            s1: Math.floor(secs / 10),
            s2: secs % 10
        }
    }

    const { m1, m2, s1, s2 } = formatTime()
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60

    return (
        <div className="scorer-timer-container">
            <div className={`timer-flip-wrapper ${isFinished ? 'finished' : ''}`}>
                <FlipNumber value={mins} />
                <div className={`timer-colon ${isActive ? 'active' : ''}`}>:</div>
                <FlipNumber value={secs} />
            </div>
            <div className="timer-controls">
                <button 
                    className={`timer-ctrl-btn ${isActive ? 'active' : ''}`} 
                    onClick={handleToggle}
                >
                    {isActive ? '暂停' : '开始'}
                </button>
                <button className="timer-ctrl-btn" onClick={() => addTime(-5)}>
                    -5M
                </button>
                <button className="timer-ctrl-btn" onClick={() => addTime(5)}>
                    +5M
                </button>
                <button className="timer-ctrl-btn" onClick={() => reset()}>
                    重置
                </button>
            </div>
        </div>
    )
}

// ========== 计分器主页面 ==========
export default function ClassMatchScorer() {
    const { matchId, scheduleId } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.user)

    // 学生/家长账号越权防御
    useEffect(() => {
        if (userInfo && isPersonal(userInfo)) {
            Toast.fail('学生与家长账号无权进行现场记分')
            navigate(-1)
        }
    }, [userInfo, navigate])

    const [matchInfo, setMatchInfo] = useState(null)
    const [schedule, setSchedule] = useState(null)
    const [homeScore, setHomeScore] = useState(0)
    const [awayScore, setAwayScore] = useState(0)
    const [syncing, setSyncing] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const initialLoaded = useRef(false)
    const wakeLockRef = useRef(null)

    // 请求屏幕常亮
    useEffect(() => {
        const requestWakeLock = async () => {
            try {
                if ('wakeLock' in navigator) {
                    wakeLockRef.current = await navigator.wakeLock.request('screen')
                }
            } catch (err) {
                console.warn('Wake Lock error:', err.message)
            }
        }

        requestWakeLock()

        // 亮屏重回时自动重连
        const handleVisibilityChange = () => {
            if (wakeLockRef.current !== null && document.visibilityState === 'visible') {
                requestWakeLock()
            }
        }
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            if (wakeLockRef.current) wakeLockRef.current.release()
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [])

    // 从 URL query 获取附加信息
    const sportType = searchParams.get('sport') || 'football'
    const matchTitle = searchParams.get('title') || '班级联赛'
    const grade = searchParams.get('grade') || ''

    // 获取赛程数据
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [matchRes, schedulesRes] = await Promise.all([
                    request.get(`/v2/sports/class-match/${matchId}`),
                    request.get(`/v2/sports/class-match/${matchId}/schedules`)
                ])
                setMatchInfo(matchRes)
                const currentSchedule = schedulesRes.find(s => s.id === Number(scheduleId))
                if (currentSchedule) {
                    setSchedule(currentSchedule)
                    setHomeScore(currentSchedule.home_score || 0)
                    setAwayScore(currentSchedule.away_score || 0)
                    initialLoaded.current = true
                }
            } catch (error) {
                Toast.fail('获取赛程数据失败')
            }
        }
        fetchData()
    }, [matchId, scheduleId])

    // 实时同步到后端
    useEffect(() => {
        if (!initialLoaded.current || !schedule) return
        if (homeScore === schedule.home_score && awayScore === schedule.away_score) return

        const syncScore = async () => {
            setSyncing(true)
            try {
                await request.patch(`/v2/sports/class-match/schedules/${scheduleId}/live-score`, {
                    home_score: homeScore,
                    away_score: awayScore
                })
            } catch (error) {
                console.error('同步失败:', error)
            } finally {
                setSyncing(false)
            }
        }

        const timer = setTimeout(syncScore, 800)
        return () => clearTimeout(timer)
    }, [homeScore, awayScore, schedule, scheduleId])

    // 提交最终比分
    const handleSubmit = async () => {
        const actualSportType = matchInfo?.sport_type || sportType
        if (actualSportType !== 'football' && actualSportType !== 'universal' && homeScore === awayScore) {
            Toast.fail('该比赛类型不允许平局，请决出胜负！')
            return
        }

        try {
            await Dialog.confirm({
                title: '确认提交',
                message: `最终比分 ${homeScore} : ${awayScore}，提交后将结算积分，确认吗？`,
            })
        } catch {
            return // 用户取消
        }

        setSubmitting(true)
        try {
            await request.post(`/v2/sports/class-match/schedules/${scheduleId}/score`, {
                home_score: homeScore,
                away_score: awayScore
            })
            Toast.success('比分提交成功！')
            setTimeout(() => navigate(-1), 1200)
        } catch (error) {
            Toast.fail('提交失败: ' + (error.response?.data?.detail || error.message))
        } finally {
            setSubmitting(false)
        }
    }

    const handleAdd = (team, amount) => {
        if (team === 'home') setHomeScore(prev => prev + amount)
        else setAwayScore(prev => prev + amount)
    }

    const handleMinus = (team) => {
        if (team === 'home') setHomeScore(prev => Math.max(0, prev - 1))
        else setAwayScore(prev => Math.max(0, prev - 1))
    }

    const isBasketball = (matchInfo?.sport_type || sportType) === 'basketball'
    const displayTitle = matchInfo?.title || matchTitle
    const displayGrade = matchInfo?.match_category === 'custom' ? '' : (matchInfo?.grade || grade)

    return (
        <div className="scorer-page">
            {/* 返回按钮 */}
            <div style={{ position: 'absolute', top: 'calc(12px + env(safe-area-inset-top))', left: 12, zIndex: 10 }}
                 onClick={() => navigate(-1)}>
                <ArrowLeft color="rgba(255,255,255,0.5)" fontSize={24} />
            </div>

            {/* 标题区域 */}
            <div className="scorer-header">
                <h1 className="scorer-title">{displayTitle}</h1>
                <div className="scorer-subtitle">{schedule?.round_name} 现场计分</div>

                {schedule && (
                    <CountdownTimer 
                        scheduleId={scheduleId}
                        initialSeconds={schedule.timer_seconds ?? 1200}
                        initialIsActive={schedule.timer_is_active ?? 0}
                    />
                )}

            </div>

            {/* 计分牌 */}
            <div className="scorer-board">
                {/* 主队 */}
                <div className="scorer-team home">
                    <div className="scorer-team-name">{schedule?.home_team_name}</div>
                    {schedule?.home_team_org && <div className="team-org">{schedule.home_team_org}</div>}
                    <div className="scorer-team-label">主队 HOME</div>
                    <FlipNumber value={homeScore} />
                    <div className="scorer-controls">
                        <button className="scorer-btn" onClick={() => handleMinus('home')}>−</button>
                        <button className="scorer-btn" onClick={() => handleAdd('home', 1)}>+1</button>
                    </div>
                    {isBasketball && (
                        <div className="scorer-controls">
                            <button className="scorer-btn" onClick={() => handleAdd('home', 2)}>+2</button>
                            <button className="scorer-btn" onClick={() => handleAdd('home', 3)}>+3</button>
                        </div>
                    )}
                </div>

                <div className="scorer-vs">VS</div>

                {/* 客队 */}
                <div className="scorer-team away">
                    <div className="scorer-team-name">{schedule?.away_team_name}</div>
                    {schedule?.away_team_org && <div className="team-org">{schedule.away_team_org}</div>}
                    <div className="scorer-team-label">客队 AWAY</div>
                    <FlipNumber value={awayScore} />
                    <div className="scorer-controls">
                        <button className="scorer-btn" onClick={() => handleMinus('away')}>−</button>
                        <button className="scorer-btn" onClick={() => handleAdd('away', 1)}>+1</button>
                    </div>
                    {isBasketball && (
                        <div className="scorer-controls">
                            <button className="scorer-btn" onClick={() => handleAdd('away', 2)}>+2</button>
                            <button className="scorer-btn" onClick={() => handleAdd('away', 3)}>+3</button>
                        </div>
                    )}
                </div>
            </div>

            {/* 底部提交 */}
            <div className="scorer-footer">
                <button
                    className="scorer-submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? '提交中...' : '✓ 结束比赛并提交比分'}
                </button>
            </div>
        </div>
    )
}
